import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { InfoGatheringStore } from '@/features/jobs/details/tax-type/info-gathering/InfoGatheringStore'
import type {
  DependentProfileEditorViewModelFactory,
  FetchDependentProfileVersionForJobFn,
  ListDependentsFn,
  PromptDeleteDependentFn,
} from '@/features/tax-profile'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import { task } from 'mobx-task'
import { action, computed, observable, runInAction } from 'mobx'
import type { RequestedSupportingItem } from '@/features/jobs/details/tax-type/info-gathering/RequestedSupportingItem'
import type { DependentsInfoSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/dependents-info/DependentsInfoSupportingItemInput'
import type { DependentProfileVersion } from '@/features/tax-profile/individual-profiles/DependentProfile'
import {
  InfiniteScrollProvider,
  syncInfiniteScrollerItems,
} from '@/infra/InfiniteScrollProvider'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { ignoreAbortError } from '@/utils/abort-util'
import { filterNonNil, withAbortSignal } from '@/utils/rx-util'
import { debounceTime, filter, map, merge, switchMap } from 'rxjs'
import { isNotNil } from '@/utils/object-util'
import type { Id } from '@/infra/id'
import type { SnackbarRetryWrapperFactory } from '@/features/shell'

/**
 * View model for a dependents info supporting item.
 */
export class DependentsInfoRequestedSupportingItemViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The supporting item input type.
   */
  readonly type = 'DependentsInfo' as const

  /**
   * The dependent profiles currently provided to this item.
   */
  @observable accessor providedDependents: DependentProfileVersion[] = []

  /**
   * Dialog for showing the dependents list.
   */
  readonly dependentsDialog = new ModalViewModel()

  /**
   * Dialog for removing a dependent.
   */
  readonly removeDialog = new ModalViewModel()

  /**
   * The dependent being removed/deleted.
   */
  @observable accessor dependentBeingRemoved: DependentProfileVersion | null =
    null

  /**
   * Infinite scroll provider for existing dependents in the workspace.
   */
  readonly existingDependentsScroller: InfiniteScrollProvider<DependentProfileVersion>

  /**
   * Editor VM for creating/editing dependents.
   */
  readonly dependentEditorVm

  constructor(
    private readonly _job: SubmittedJob,
    public readonly item: RequestedSupportingItem<DependentsInfoSupportingItemInput>,
    private readonly _infoGatheringStore: InfoGatheringStore,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    createDependentProfileEditorViewModel: DependentProfileEditorViewModelFactory,
    private readonly _fetchDependentProfileVersionForJob: FetchDependentProfileVersionForJobFn,
    private readonly _listDependents: ListDependentsFn,
    private readonly _promptDeleteDependent: PromptDeleteDependentFn,
  ) {
    this.dependentEditorVm = createDependentProfileEditorViewModel({
      isReadOnly: () => this.isReadOnly,
      onSaved: (profile) => this.provideDependent(profile),
      onRemove: (profile) => {
        this.showRemoveDependentDialog(profile)
      },
    })

    this.existingDependentsScroller = new InfiniteScrollProvider({
      fetchItems: ({ paging, signal }) =>
        this._listDependents({ paging }, signal),
      limit: 1000,
    })

    // Reload the provided dependents when the input changes.
    this._lifecycle.manage(
      this.item.input.providedChanges$.pipe(
        debounceTime(200),
        filter((providedList) =>
          // If any of the provided dependents in the source do not exist
          // in the view model's list of provided dependents, then it's a
          // remote change, and that's when we want to re-fetch.
          providedList.some(
            (p) =>
              !this.providedDependents.some((d) =>
                d.equalsIdentity(p.profileId, p.profileVersion),
              ),
          ),
        ),
        switchMap(() => withAbortSignal((signal) => this._fetchCore(signal))),
      ),
    )

    // Remove existing dependent from the list when delisted
    const delisted$ = this.dependentEditorVm.dependentDelisted$.pipe(
      map((id) =>
        this.existingDependentsScroller.items.find((d) => d.id === id),
      ),
      filterNonNil(),
    )

    // Remove older dependent versions when a new version of the same
    // dependent is added
    const dependentVersionSuperseded$ =
      this.dependentEditorVm.dependentAdded$.pipe(
        map((newVersion) =>
          this.existingDependentsScroller.items.find(
            (loaded) =>
              loaded.id === newVersion.id &&
              loaded.version !== newVersion.version,
          ),
        ),
        filterNonNil(),
      )
    const dependentVersionRemoved$ = merge(
      dependentVersionSuperseded$,
      delisted$,
    )

    this._lifecycle.manage(
      syncInfiniteScrollerItems({
        scroller: this.existingDependentsScroller,
        additions$: this.dependentEditorVm.dependentAdded$,
        removals$: dependentVersionRemoved$,
      }),
    )
  }

  /**
   * Whether the item is read-only.
   */
  @computed get isReadOnly() {
    return this._job.status === 'Closed'
  }

  /**
   * Whether the item is considered completed.
   */
  @computed get isCompleted() {
    return (
      this.item.isCompleted &&
      // Checks the loaded profiles, avoids a flash when dependents are updated
      // in real-time from another device.
      this.providedDependents.length > 0
    )
  }

  /**
   * Whether the dependent list is empty.
   */
  @computed get isEmpty() {
    return this.providedDependents.length === 0
  }

  /**
   * Dependents that can be added.
   */
  @computed get existingAvailableDependents() {
    const alreadyAdded = new Set(this.providedDependents.map((d) => d.id))
    return this.existingDependentsScroller.items.filter(
      (d) => !alreadyAdded.has(d.id),
    )
  }

  /**
   * Whether we're fetching data needed to display the editor.
   */
  @computed get isFetchingEditorData() {
    return this.fetch.pending || this.existingDependentsScroller.fetch.pending
  }

  /**
   * Fetches the provided dependent profiles for this job.
   */
  fetch = task(async (signal?: AbortSignal) => {
    await this._fetchCore(signal)
  })

  /**
   * Core fetch logic that doesn't reset task state.
   * @private
   */
  private async _fetchCore(signal?: AbortSignal) {
    const withRetry = this._createSnackbarRetryWrapper(
      'Unable to retrieve dependent profile information',
      signal,
    )

    const dependents = await withRetry(
      async () =>
        await Promise.all(
          this.item.input.dependents.map(
            async (dep) =>
              await this._fetchDependentProfileVersionForJob(
                this._job.id,
                dep.profileId,
                signal,
              ),
          ),
        ),
    )

    signal?.throwIfAborted()

    this.setProvidedDependents(dependents.filter(isNotNil))
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch(this._lifecycle.signal).catch(ignoreAbortError)
    void this.existingDependentsScroller
      .fetch({ signal: this._lifecycle.signal })
      .catch(ignoreAbortError)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.dependentBeingRemoved = null
    this.removeDialog.close()
    this.dependentEditorVm.closeDialog()
    this.dependentsDialog.close()
  }

  /**
   * Sets the provided dependents.
   */
  @action.bound
  setProvidedDependents(dependents: DependentProfileVersion[]) {
    this.providedDependents = dependents
  }

  /**
   * Shows the dependents dialog.
   */
  @action.bound
  showDependentsDialog() {
    this.dependentsDialog.show()
  }

  /**
   * Shows the dependent editor for creating a new dependent.
   */
  @action.bound
  showCreateDependentDialog() {
    this.dependentEditorVm.showDialog(null)
  }

  /**
   * Shows the dependent editor for editing an existing dependent.
   */
  @action.bound
  showEditDependentDialog(dependent: DependentProfileVersion) {
    this.dependentEditorVm.showDialog(dependent)
  }

  /**
   * Shows the remove-dependent dialog.
   */
  @action.bound
  showRemoveDependentDialog(dependent: DependentProfileVersion) {
    this.dependentBeingRemoved = dependent
    this.removeDialog.show()
  }

  /**
   * Adds an existing dependent from the workspace to this job.
   */
  addExistingDependent = task.resolved(
    async (dependent: DependentProfileVersion) => {
      if (this.providedDependents.some((d) => d.id === dependent.id)) {
        return
      }

      await this._infoGatheringStore.provideDependent(
        this._job.id,
        this.item,
        dependent,
      )
      this.setProvidedDependents([...this.providedDependents, dependent])
    },
  )

  /**
   * Removes a dependent from this job.
   */
  removeDependentFromJob = task.resolved(async (dependentId: Id) => {
    await this._infoGatheringStore.removeDependent(
      this._job.id,
      this.item,
      dependentId,
    )

    const newDependents = this.providedDependents.filter(
      (d) => d.id !== dependentId,
    )

    runInAction(() => {
      this.setProvidedDependents(newDependents)
      this.dependentEditorVm.closeDialog()
      this.removeDialog.close()
      this.dependentBeingRemoved = null
    })
  })

  /**
   * Prompts for and performs permanent deletion of a dependent.
   */
  promptDeleteDependentPermanently = task.resolved(
    async (dependent: DependentProfileVersion) => {
      const wasDeleted = await this._promptDeleteDependent(
        dependent,
        this._lifecycle.signal,
      )

      if (wasDeleted) {
        // Remove dependent from the job.
        await this.removeDependentFromJob(dependent.id)
      }
    },
  )

  /**
   * Provides a new dependent version to the job.
   */
  provideDependent = task.resolved(
    async (dependent: DependentProfileVersion) => {
      await this._infoGatheringStore.provideDependent(
        this._job.id,
        this.item,
        dependent,
      )

      this.setProvidedDependents([
        ...this.providedDependents.filter(
          (existing) => existing.id !== dependent.id,
        ),
        dependent,
      ])
    },
  )
}
